// client/src/pages/pmPortal/TenantMaintenancePage.js
// The one write in either portal.
//
// The form is short on purpose. A resident with no heat should not be filling in a
// category taxonomy; the office triages. Choosing "emergency" raises the priority a
// human sees, and nothing else — it does not unlock spending, which is the owner's
// limit and the managing broker's decision.
import React, { useCallback, useEffect, useState } from 'react';
import { usePmPortalApiClient } from '../../hooks/usePmPortalApiClient';
import { PageHeader, Panel, Table, Loading, Notice, Pill, Input, Select, Textarea, Button,
    fmtDate, titleCase, statusTone } from '../../components/secure/pm/PMKit';

const TenantMaintenancePage = () => {
    const api = usePmPortalApiClient();
    const [orders, setOrders] = useState(null);
    const [leases, setLeases] = useState([]);
    const [error, setError] = useState('');
    const [saved, setSaved] = useState('');
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({ lease_id: '', title: '', description: '', priority: 'routine' });

    const load = useCallback(() => {
        api.get('/api/pm-portal/tenant/work-orders').then(setOrders).catch(e => setError(e.message));
    }, [api]);

    useEffect(() => {
        load();
        api.get('/api/pm-portal/tenant/leases')
            .then(rows => {
                const active = rows.filter(l => ['active', 'holdover', 'month_to_month'].includes(l.status));
                setLeases(active);
                if (active.length === 1) setForm(f => ({ ...f, lease_id: active[0].lease_id }));
            })
            .catch(() => { /* the list below reports any failure */ });
    }, [api, load]);

    const submit = async (e) => {
        e.preventDefault();
        setError('');
        setSaved('');
        setSaving(true);
        try {
            const body = { ...form };
            if (!body.lease_id) delete body.lease_id;
            const result = await api.post('/api/pm-portal/tenant/work-orders', body);
            setSaved(result.message);
            setForm({ lease_id: leases.length === 1 ? leases[0].lease_id : '', title: '', description: '', priority: 'routine' });
            load();
        } catch (err) {
            setError(err.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <PageHeader title="Maintenance" subtitle="Report a problem, and follow what happens to it." />

            {saved && <Notice tone="good" title="Request logged" message={saved} onDismiss={() => setSaved('')} />}
            {error && <Notice tone="bad" title="Could not log your request" message={error} onDismiss={() => setError('')} />}

            <Panel title="Report a problem">
                {leases.length === 0 ? (
                    <p className="text-sm text-slate-500 py-2">
                        Maintenance requests can only be raised on an active lease. Please contact the office.
                    </p>
                ) : (
                    <form onSubmit={submit}>
                        {leases.length > 1 && (
                            <Select
                                label="Which unit"
                                value={form.lease_id}
                                onChange={e => setForm({ ...form, lease_id: e.target.value })}
                                required
                                options={[
                                    { value: '', label: 'Choose a unit…' },
                                    ...leases.map(l => ({
                                        value: l.lease_id,
                                        label: `${l.property_name}${l.unit_number ? ` · Unit ${l.unit_number}` : ''}`,
                                    })),
                                ]}
                            />
                        )}
                        <Input
                            label="What is wrong"
                            value={form.title}
                            onChange={e => setForm({ ...form, title: e.target.value })}
                            placeholder="e.g. No hot water in the kitchen"
                            maxLength={255}
                            required
                        />
                        <Textarea
                            label="Anything else that would help"
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            placeholder="When it started, whether it is constant, and the best times to get access."
                            rows={4}
                        />
                        <Select
                            label="How urgent is it"
                            value={form.priority}
                            onChange={e => setForm({ ...form, priority: e.target.value })}
                            hint="Emergency means a risk to health, safety or the building — no heat, no water, a leak that is spreading."
                            options={[
                                { value: 'routine', label: 'Routine' },
                                { value: 'urgent', label: 'Urgent' },
                                { value: 'emergency', label: 'Emergency' },
                            ]}
                        />
                        <Button type="submit" disabled={saving}>
                            {saving ? 'Sending…' : 'Send request'}
                        </Button>
                        <p className="text-xs text-slate-500 mt-3">
                            You will be given notice before anyone enters your home, except where the law
                            allows immediate entry in an emergency.
                        </p>
                    </form>
                )}
            </Panel>

            <Panel title="Your requests">
                {!orders ? <Loading /> : (
                    <Table
                        columns={[
                            { key: 'wo_no', label: 'No.', render: r => r.wo_no || `#${r.work_order_id}` },
                            { key: 'title', label: 'Problem' },
                            { key: 'unit_number', label: 'Unit' },
                            { key: 'priority', label: 'Priority',
                              render: r => <Pill tone={r.priority === 'emergency' ? 'bad' : r.priority === 'urgent' ? 'warn' : 'slate'}>{titleCase(r.priority)}</Pill> },
                            { key: 'status', label: 'Status', render: r => <Pill tone={statusTone(r.status)}>{titleCase(r.status)}</Pill> },
                            { key: 'reported_on', label: 'Reported', render: r => fmtDate(r.reported_on) },
                            { key: 'entry_notice_given_on', label: 'Entry notice', render: r => fmtDate(r.entry_notice_given_on) },
                            { key: 'completed_on', label: 'Completed', render: r => fmtDate(r.completed_on) },
                        ]}
                        rows={orders}
                        empty="You have not reported anything yet."
                    />
                )}
            </Panel>
        </>
    );
};

export default TenantMaintenancePage;
